import { useState, useEffect, useCallback } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { SessionIndicators } from "@shared-core/data/feature-session-types";

interface UseSessionIndicatorsOptions {
  repository: string;
  branchName: string;
  enabled: boolean;
  intervalMs?: number;
}

interface UseSessionIndicatorsReturn {
  indicators: SessionIndicators | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useSessionIndicators({
  repository,
  branchName,
  enabled,
  intervalMs = 5000,
}: UseSessionIndicatorsOptions): UseSessionIndicatorsReturn {
  const dataAdapter = useDataAdapter();
  const [indicators, setIndicators] = useState<SessionIndicators | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIndicators = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const result = await dataAdapter.getSessionIndicators(
        repository,
        branchName,
      );
      setIndicators(result);
      setError(null);
    } catch (err) {
      console.error("Error fetching session indicators:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch indicators",
      );
    } finally {
      setLoading(false);
    }
  }, [dataAdapter, repository, branchName, enabled]);

  useEffect(() => {
    if (!enabled) {
      setIndicators(null);
      return;
    }

    fetchIndicators();
    const id = setInterval(fetchIndicators, intervalMs);
    return () => clearInterval(id);
  }, [fetchIndicators, intervalMs, enabled]);

  return { indicators, loading, error, refresh: fetchIndicators };
}
